import { ProviderRole } from '@bariendo/lib/constants';
import {
  ActivityDefinition,
  Appointment,
  Coding,
  Encounter,
  Organization,
  Patient,
  Practitioner,
  PractitionerRole,
  Reference,
  Location,
  Slot,
} from '@medplum/fhirtypes';
import { Event } from 'react-big-calendar';

interface AppointmentDetailBase {
  appointmentType?: string;
  status?: string;
  location?: string;
  providers: (Practitioner | Reference<Practitioner>)[];
  patients: (Patient | Reference<Patient>)[];
  contactType?: 'Video Call' | 'Phone Call' | 'In-Person';
  videoCallUrl?: string;
  /** Internal/admin video URL (e.g., Zoom startUrl) */
  videoCallAdminUrl?: string;
  virtualServicePhone?: string;
  compartments?: { reference?: string }[];
}

export interface AvailabilityBackgroundEvent extends Event {
  start: Date;
  end: Date;
}

export interface AppointmentWithEncounter extends Appointment {
  encounter?: Encounter;
}

export interface AppointmentEvent extends Event, AppointmentDetailBase {
  appointmentResource: Appointment;
  partner?: Reference<Organization>;
}

/** Calendar block backed by a `Slot` with `status: busy-unavailable` (no Appointment). */
export interface BlockSlotEvent extends Event, AppointmentDetailBase {
  slotResource: Slot;
  /** Day-view column: Practitioner reference string */
  resource?: string;
}

export interface BlockSlotDetailData {
  slotResource: Slot;
  providers: (Practitioner | Reference<Practitioner>)[];
  title?: string;
}

export interface AppointmentDetailData extends AppointmentDetailBase {
  appointmentResource: AppointmentWithEncounter;
  title?: string;
  partner?: Reference<Organization>;
}

export interface PopoverPosition {
  x: number;
  y: number;
  eventElement?: HTMLElement;
}

export type UseFindAvailabilityFilters = {
  practitionersRoles?: Reference<PractitionerRole>[];
  roles?: ProviderRole[];
  serviceTypes?: string[];
  locations?: string[];
};

export type PractitionerWithRole = Practitioner & { roleLabel?: string; roleCode?: ProviderRole };

export type GuestFormData = Patient | PractitionerRole;

export enum AppointmentRepeatingInterval {
  DAILY = 'daily',
  WEEKLY = 'weekly',
  MONTHLY = 'monthly',
}

/** Recurrence pattern for the appointment drawer (single Select). */
export type AppointmentRecurrencePreset = 'none' | 'daily' | 'weekly' | 'biweekly' | 'monthly';

export interface AppointmentFormData {
  appointmentType: ActivityDefinition | null;
  appointmentTypeCode: string | null;
  provider: PractitionerWithRole | null;
  partner: Organization | null;
  guests: GuestFormData[];
  visitType: Coding;
  location: Location | null;
  timezone: string | null;
  callType: 'video' | 'phone' | null;
  videoLink: string;
  videoLinkType: 'zoom' | 'custom';
  phoneNumber: string;
  date: Date | null;
  startTime: string;
  /** Local end time (HH:mm) when the appointment has an explicit end instant. */
  endTime?: string;
  notes: string;
  notesJson: object | null;
  appointmentReference: string | null;
  /** When editing a series occurrence, controls which occurrences are updated. */
  editScope?: 'single' | 'thisAndFollowing' | 'all';
  isRepeating: boolean;
  /** Primary recurrence UI value; drives `isRepeating` and backend `RecurrenceSettings`. */
  recurrencePreset: AppointmentRecurrencePreset;
  repeatingInterval: AppointmentRepeatingInterval;
  repeatTimes: number;
  monthlyNth: 1 | 2 | 3 | 4 | 5;
  /** dayjs weekday: 0 = Sunday .. 6 = Saturday */
  monthlyWeekday: number;
}
